/**
 * How numbers read on screen. One place, so "1.2K" means the same thing on
 * every card and every table.
 */

const whole = new Intl.NumberFormat('en-GB');

/**
 * Hand-rolled rather than Intl's compact notation, whose casing ("12.9k" or
 * "12.9K") depends on the browser's ICU data. One spelling everywhere.
 */
function compact(n: number): string {
  const [div, suffix] = Math.abs(n) >= 1_000_000 ? [1_000_000, 'M'] : [1_000, 'K'];
  return `${(n / div).toFixed(1).replace(/\.0$/, '')}${suffix}`;
}

/** 950 → "950", 1,284 → "1,284", 12,900 → "12.9K". Tables stay exact; tiles compact past 10,000. */
export function formatCount(n: number | null | undefined, opts: { compact?: boolean } = {}): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return opts.compact && Math.abs(n) >= 10_000 ? compact(n) : whole.format(n);
}

/** 0.3333 → "33%". */
export function formatPercent(ratio: number | null | undefined, digits = 0): string {
  if (ratio === null || ratio === undefined) return '—';
  return `${(ratio * 100).toFixed(digits)}%`;
}

/** 980 → "16m 20s", 45 → "45s", 3700 → "1h 1m". */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/**
 * Period-over-period change as a signed percentage and a direction. Null when
 * there is nothing to compare against — never "+∞%".
 */
export function formatChange(current: number, previous: number): { label: string; direction: 'up' | 'down' | 'flat' } | null {
  if (previous === 0) return null;
  const change = (current - previous) / previous;
  if (Math.abs(change) < 0.005) return { label: '0%', direction: 'flat' };
  return { label: `${change > 0 ? '+' : '−'}${Math.abs(change * 100).toFixed(0)}%`, direction: change > 0 ? 'up' : 'down' };
}

const relative = new Intl.RelativeTimeFormat('en-GB', { numeric: 'auto' });

/** "just now", "5 minutes ago", "yesterday", "3 days ago". */
export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return 'never';
  const seconds = Math.round((Date.parse(iso) - now.getTime()) / 1000);
  const abs = Math.abs(seconds);
  if (abs < 45) return 'just now';
  if (abs < 3_600) return relative.format(Math.round(seconds / 60), 'minute');
  if (abs < 86_400) return relative.format(Math.round(seconds / 3_600), 'hour');
  return relative.format(Math.round(seconds / 86_400), 'day');
}

const dayFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const longDay = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

/** "2026-10-01" → "1 Oct". The day string is already a London day; format it as-is. */
export function formatDay(day: string, long = false): string {
  return (long ? longDay : dayFormat).format(new Date(`${day}T00:00:00Z`));
}

/** Two initials for an avatar. */
export function initials(nameOrEmail: string): string {
  const base = nameOrEmail.split('@')[0] as string;
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (parts[1]?.[0] ?? '')).toUpperCase();
}
